import axios from "axios";
import React from "react";
import { useQuery } from "react-query";
import { Pause, Play } from "lucide-react";
import { useAudioPlayer } from "./GlobalAudioPlayer";
import { useT } from "@jammable/ui-core/src/i18n";

const LibraryDemosSection = ({ modelData }) => {
  const t = useT();
  const { data: libraryDemos, isLoading } = useQuery(
    ["library-demos", modelData?._id],
    async () => {
      const res = await axios.get(`/api/model/library-demos/${modelData?._id}`);
      return res.data;
    },
    {
      enabled: !!modelData?._id,
    },
  );

  const { playAudio, pauseAudio, isPlaying, info } = useAudioPlayer();

  const heading = (
    <div className="tw-flex tw-flex-col tw-gap-1">
      <h2 className="jam-display tw-m-0 tw-text-[22px] tw-font-extrabold tw-text-jam-ink md:tw-text-[28px]">
        {t("voice.library.title")}
      </h2>
      <span className="tw-text-[13px] tw-font-bold tw-text-jam-muted">
        {t("voice.library.sub", { name: modelData?.artistname })}
      </span>
    </div>
  );

  if (isLoading) {
    return (
      <section className="tw-flex tw-w-full tw-flex-col tw-gap-3.5">
        {heading}
        <div className="tw-grid tw-grid-cols-2 tw-gap-3 md:tw-grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="tw-h-[170px] tw-animate-pulse tw-rounded-[26px] tw-bg-jam-lav"
            />
          ))}
        </div>
      </section>
    );
  }

  if (!libraryDemos?.songDetails?.length) {
    return null;
  }

  return (
    <section className="tw-flex tw-w-full tw-flex-col tw-gap-3.5">
      {heading}
      <div className="tw-grid tw-grid-cols-2 tw-gap-3 md:tw-grid-cols-4">
        {libraryDemos?.songDetails?.map((song) => {
          const demoUrl = song?.demoInfo?.convertedUrl;
          const songName =
            song?.metadata?.title || t("voice.library.unknownSong");
          const artistName =
            song?.metadata?.artist || t("voice.library.unknownArtist");
          const songImage = song?.metadata?.image?.url;
          const isCurrentlyPlaying =
            isPlaying && info?.id === `${modelData?._id}-library-${song?._id}`;

          if (!demoUrl) return null;

          return (
            <div
              key={song?._id}
              className="tw-flex tw-min-w-0 tw-flex-col tw-gap-2 tw-rounded-[26px] tw-border-2 tw-border-solid tw-border-jam-line tw-p-2.5"
            >
              <div className="tw-relative tw-h-[110px] tw-overflow-hidden tw-rounded-[18px] tw-bg-jam-lav">
                {songImage && (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={songImage}
                    alt=""
                    loading="lazy"
                    className="tw-h-full tw-w-full tw-object-cover"
                  />
                )}
                <button
                  type="button"
                  aria-label={
                    isCurrentlyPlaying
                      ? t("voice.pause", { name: songName })
                      : t("voice.play", { name: songName })
                  }
                  onClick={() => {
                    if (isCurrentlyPlaying) {
                      pauseAudio();
                    } else {
                      playAudio({
                        audio: demoUrl,
                        info: {
                          image: modelData?.image?.url || "/default-album.jpg",
                          name: songName,
                          id: `${modelData?._id}-library-${song?._id}`,
                          artistNames: `${modelData?.artistname} • ${artistName}`,
                        },
                      });
                    }
                  }}
                  className="tw-absolute tw-bottom-2 tw-right-2 tw-flex tw-h-[38px] tw-w-[38px] tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-white tw-text-jam-purple"
                >
                  {isCurrentlyPlaying ? (
                    <Pause size={13} fill="currentColor" strokeWidth={0} />
                  ) : (
                    <Play size={13} fill="currentColor" strokeWidth={0} />
                  )}
                </button>
              </div>

              <div className="tw-min-w-0 tw-px-1 tw-pb-0.5">
                <div className="tw-truncate tw-text-sm tw-font-extrabold tw-text-jam-ink">
                  {songName}
                </div>
                <div className="tw-truncate tw-text-xs tw-font-semibold tw-text-jam-muted">
                  {artistName}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default LibraryDemosSection;
